import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import cn from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { useMemo } from 'react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { getContrastTextColor } from '@utils/helpers/color-utils.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';

/* eslint-disable-next-line no-restricted-syntax -- Alert badge semantic danger color, same as StatWidget danger */
const ALERT_BADGE_BG = '#EF4444';

export type NavItemDef = {
  id?: string;
  name: string;
  href: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children?: NavItemDef[];
  isSubItem?: boolean;
  badge?: number;
};

interface NavItemRowProps {
  item: NavItemDef;
  expanded?: boolean;
  onToggleExpand?: () => void;
  depth?: number;
  /** Icon-only rail variant used by the editor sidebar (#2230). */
  collapsed?: boolean;
}

export function NavItemRow({
  item,
  expanded,
  onToggleExpand,
  depth = 0,
  collapsed = false
}: NavItemRowProps) {
  const { getColour, getBrandTint, theme } = useThemeContext();
  const location = useLocation();

  const styles = useMemo(() => {
    const activeBg = getColour('activeText');
    const activeFg = getContrastTextColor(activeBg);
    return StyleSheet.create({
      link: {
        color: getColour('normalText'),
        transition: 'background-color 0.15s ease, color 0.15s ease',
        ':hover': {
          color: getColour('activeText'),
          backgroundColor: getBrandTint(0.06)
        }
      },
      active: {
        color: activeFg,
        backgroundColor: activeBg
      },
      activeParent: {
        color: getColour('activeText')
      }
    });
  }, [getColour, getBrandTint, theme]);

  if (collapsed) {
    // Collapsed rail: leaf items only. Parents render as icon links that
    // navigate to the parent path (mirrors expanded behaviour on click).
    return (
      <li className="flex justify-center">
        <Tooltip label={item.name} placement="right">
          <NavLink
            to={item.href}
            end
            aria-label={item.name}
            className={({ isActive }) =>
              cn(
                css(styles.link),
                'relative inline-flex items-center justify-center h-10 w-10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary',
                isActive && css(styles.active)
              )
            }
          >
            {item.icon && (
              <item.icon className="h-5 w-5 shrink-0 text-inherit" />
            )}
            {!!item.badge && item.badge > 0 && (
              <span
                className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none text-white tabular-nums"
                style={{ backgroundColor: ALERT_BADGE_BG }}
              >
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </NavLink>
        </Tooltip>
      </li>
    );
  }

  const baseClass = cn(
    css(styles.link),
    'flex items-center gap-x-3 rounded-2xl text-sm font-medium leading-6 focus:outline-none',
    depth === 0 ? 'px-4 py-1.5' : 'pl-11 pr-4 py-2 text-[13px]'
  );

  if (item.children) {
    const isChildActive = item.children.some(child =>
      location.pathname.startsWith(child.href)
    );
    return (
      <li>
        <div
          className={cn(
            css(styles.link),
            isChildActive && css(styles.activeParent),
            'group flex items-center rounded-2xl pr-2 text-sm font-medium leading-6'
          )}
        >
          <NavLink
            to={item.href}
            end
            className={() =>
              cn(
                'flex flex-1 items-center gap-x-3 px-4 py-1.5 text-inherit rounded-2xl focus:outline-none'
              )
            }
            onClick={() => onToggleExpand?.()}
          >
            {item.icon && (
              <item.icon className="h-5 w-5 shrink-0 text-inherit" />
            )}
            <span className="flex-1 text-left">{item.name}</span>
            {!!item.badge && item.badge > 0 && (
              <span
                className="flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none text-white tabular-nums"
                style={{ backgroundColor: ALERT_BADGE_BG }}
              >
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </NavLink>
          <button
            type="button"
            aria-label={expanded ? 'Collapse' : 'Expand'}
            aria-expanded={expanded}
            onClick={e => {
              e.stopPropagation();
              onToggleExpand?.();
            }}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-2xl text-inherit hover:opacity-70 focus:outline-none"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-inherit" />
            ) : (
              <ChevronRight className="h-4 w-4 text-inherit" />
            )}
          </button>
        </div>
        {expanded &&
          item.children.map(child => (
            <NavItemRow key={child.href} item={child} depth={1} />
          ))}
      </li>
    );
  }

  return (
    <li>
      <NavLink
        to={item.href}
        end
        className={({ isActive }) =>
          cn(baseClass, isActive && css(styles.active))
        }
      >
        {item.icon && (
          <item.icon
            className={cn(
              'shrink-0 text-inherit',
              depth === 0 ? 'h-5 w-5' : 'h-4 w-4'
            )}
          />
        )}
        <span className={item.badge && item.badge > 0 ? 'flex-1' : undefined}>
          {item.name}
        </span>
        {!!item.badge && item.badge > 0 && (
          <span
            className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full px-1 pt-px text-[10px] font-bold leading-none text-white tabular-nums"
            style={{ backgroundColor: ALERT_BADGE_BG }}
          >
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </NavLink>
    </li>
  );
}
